import React, { useEffect, useRef, useState } from "react";
import { Box, Button, CheckBox, Heading, Layer, Select, Spinner, Text } from "grommet";
import { FormClose, Image as ImageIcon, View } from "grommet-icons";
import { useTranslation } from "react-i18next";
import HotelService from "../../../services/hotel.service";
import PhotoService from "../../../services/photo.service";
import { API_URL, SERVER_URL } from "../../../services/api";
import { presentToastSuccess, presentToastErrorContent } from "../../shared/Toast";
import { createHeaderArtwork, compressHeaderSource, drawHeaderArtwork, dragHeaderCrop, clampPosition,
  loadHeaderImage, HEADER_TYPES, MAX_HEADER_BYTES } from "./emailHeaderArtwork";
import "./EmailHeaderSettings.scss";

const initialDesign = { mode: "default", x: 50, y: 50, zoom: 1, showLogo: true, cardPosition: "right" };
const savedState = hotel => ({ header: hotel.emailHeader || "", source: hotel.emailHeaderSource || "",
  revision: hotel.emailHeaderRevision || 0,
  design: { ...initialDesign, ...(hotel.emailHeader ? { mode: "custom", showLogo: false } : {}), ...hotel.emailHeaderDesign } });
export function defaultEmailHeader(logo) {
  const url = logo && !logo.startsWith("sec_") ? PhotoService.getURL(logo) : "";
  return url && url.includes("/image/") ? url.replace("/image/", "/image/header-logo/") : `${SERVER_URL}/images/email-header.jpg`;
}
export function customEmailHeader(header) {
  if (/^email-header-[a-f0-9-]+\.jpg$/.test(header || "")) return `${API_URL}image/${header}`;
  return /^https?:\/\//.test(header || "") ? header : "";
}

function HeaderCanvas({ source, design, logo, onChange, disabled, label, helpId }) {
  const canvas = useRef(null);
  const drag = useRef(null);
  useEffect(() => {
    if (canvas.current && source) drawHeaderArtwork(canvas.current, source, design, design.showLogo ? logo : null);
  }, [source, design, logo]);
  const interactive = Boolean(onChange && !disabled);
  return <canvas ref={canvas} className={`email-appearance__canvas${interactive ? " email-appearance__canvas--draggable" : ""}`}
    width={1200} height={543} role="img" aria-label={label} aria-describedby={interactive ? helpId : undefined} tabIndex={interactive ? 0 : undefined}
    onPointerDown={event => {
      if (!interactive || event.button !== 0) return;
      event.currentTarget.setPointerCapture(event.pointerId);
      drag.current = { x: event.clientX, y: event.clientY, design, width: event.currentTarget.getBoundingClientRect().width };
    }}
    onPointerMove={event => {
      if (!interactive || !drag.current) return;
      onChange(dragHeaderCrop(source, drag.current.design, event.clientX - drag.current.x, event.clientY - drag.current.y, drag.current.width));
    }}
    onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; }} onLostPointerCapture={() => { drag.current = null; }}
    onKeyDown={event => {
      if (!interactive || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
      event.preventDefault();
      const axis = ["ArrowLeft", "ArrowRight"].includes(event.key) ? "x" : "y";
      const delta = ["ArrowLeft", "ArrowUp"].includes(event.key) ? 2 : -2;
      onChange({ ...design, [axis]: clampPosition(design[axis] + delta) });
    }} />;
}

export default function EmailHeaderSettings({ hotel, enabled, onUpgrade, onSaved }) {
  const { t } = useTranslation();
  const tr = key => t(`emailAppearance.${key}`);
  const [saved, setSaved] = useState(() => savedState(hotel));
  const [design, setDesign] = useState(() => savedState(hotel).design);
  const [source, setSource] = useState(null);
  const [sourceFile, setSourceFile] = useState(null);
  const [logo, setLogo] = useState(null);
  const [preview, setPreview] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [logoLoading, setLogoLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);
  const input = useRef(null);
  const selection = useRef(0);
  const mounted = useRef(true);
  const savedProps = useRef(`${hotel.emailHeader || ""}:${hotel.emailHeaderRevision || 0}`);
  const fallback = defaultEmailHeader(hotel.logo);
  const logoAvailable = Boolean(hotel.logo && !hotel.logo.startsWith("sec_"));
  const dirty = Boolean(sourceFile) || JSON.stringify(design) !== JSON.stringify(saved.design);
  const mode = enabled ? design.mode : "default";
  const busy = saving || loading;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; selection.current += 1; };
  }, []);
  useEffect(() => {
    const signature = `${hotel.emailHeader || ""}:${hotel.emailHeaderRevision || 0}`;
    if (savedProps.current === signature) return;
    savedProps.current = signature;
    const next = savedState(hotel);
    setSaved(next); setDesign(next.design); setSource(null); setSourceFile(null);
    selection.current += 1;
  }, [hotel]);

  useEffect(() => {
    if (!enabled || mode !== "custom" || source || sourceFile || (!saved.source && !saved.header)) return undefined;
    let active = true;
    const version = ++selection.current;
    setLoading(true); setError("");
    (async () => {
      let url;
      try {
        const response = await HotelService.getEmailHeaderSource();
        url = URL.createObjectURL(response.data);
        const image = await loadHeaderImage(url);
        if (active && selection.current === version) setSource(image);
      } catch { if (active && selection.current === version) setError(t("emailAppearance.sourceError")); }
      finally {
        if (url) URL.revokeObjectURL(url);
        if (active && selection.current === version) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [enabled, mode, source, sourceFile, saved.source, saved.header, reload, t]);

  useEffect(() => {
    setLogo(null);
    if (!logoAvailable || mode !== "custom" || !design.showLogo) { setLogoLoading(false); return undefined; }
    let active = true;
    setLogoLoading(true);
    loadHeaderImage(PhotoService.getURL(hotel.logo)).then(image => {
      if (active) setLogo(image);
    }, () => { if (active) setError(t("emailAppearance.logoError")); })
      .finally(() => { if (active) setLogoLoading(false); });
    return () => { active = false; };
  }, [hotel.logo, logoAvailable, mode, design.showLogo, t]);

  const requestUpload = () => {
    if (!enabled) { onUpgrade(); return; }
    input.current.click();
  };
  const selectImage = async event => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length || !enabled || saving) return;
    if (files.length !== 1 || !HEADER_TYPES.includes(files[0].type) || files[0].size > MAX_HEADER_BYTES) {
      setError(tr("invalidImage")); return;
    }
    const version = ++selection.current;
    const url = URL.createObjectURL(files[0]);
    let compressedUrl;
    setError(""); setLoading(true);
    try {
      const original = await loadHeaderImage(url);
      const blob = await compressHeaderSource(original);
      compressedUrl = URL.createObjectURL(blob);
      const image = await loadHeaderImage(compressedUrl);
      if (version !== selection.current) return;
      setSource(image);
      setSourceFile(new File([blob], "email-header-source.jpg", { type: "image/jpeg" }));
      setDesign({ ...initialDesign, mode: "custom" });
    } catch { if (version === selection.current) setError(tr("loadError")); }
    finally {
      URL.revokeObjectURL(url);
      if (compressedUrl) URL.revokeObjectURL(compressedUrl);
      if (version === selection.current) setLoading(false);
    }
  };
  const save = async () => {
    if (!enabled || busy || !dirty || (mode === "custom" && (!source || logoLoading || (design.showLogo && logoAvailable && !logo)))) return;
    setSaving(true); setError("");
    try {
      const artwork = mode === "custom" ? await createHeaderArtwork(source, design, design.showLogo ? logo : null) : null;
      const response = await HotelService.uploadEmailHeader(artwork ? new File([artwork], "email-header.jpg", { type: "image/jpeg" }) : null,
        { source: mode === "custom" ? sourceFile : null, design, revision: saved.revision });
      const data = response.data;
      if (!data.success || !data.design || !Number.isInteger(data.revision) || (mode === "custom" && !customEmailHeader(data.filename))) throw new Error(tr("saveError"));
      if (!mounted.current) return;
      const next = { header: data.filename || "", source: data.source || "", design: data.design, revision: data.revision };
      setSaved(next); setDesign(next.design); setSourceFile(null);
      if (mode !== "custom") setSource(null);
      savedProps.current = `${next.header}:${next.revision}`;
      if (onSaved) onSaved({ emailHeader: next.header, emailHeaderSource: next.source, emailHeaderDesign: next.design, emailHeaderRevision: next.revision });
      presentToastSuccess(t("offers.emailHeaderSaved"));
    } catch (failure) {
      if (mounted.current) { setError(tr(failure.response?.status === 409 ? "conflictError" : "saveError")); presentToastErrorContent(failure); }
    } finally { if (mounted.current) setSaving(false); }
  };
  const cancel = () => {
    selection.current += 1;
    setSource(null); setSourceFile(null); setDesign(saved.design); setError(""); setLoading(false); setReload(value => value + 1);
  };
  const header = interactive => mode === "none" ? null : mode === "custom" && source ?
    <HeaderCanvas source={source} design={design} logo={logo} onChange={interactive ? setDesign : undefined} disabled={busy}
      label={tr(interactive ? "dragLabel" : "headerAlt")} helpId="email-header-drag-help" /> :
    <img className="email-appearance__thumbnail" src={mode === "custom" ? customEmailHeader(saved.header) || fallback : fallback} alt={tr("headerAlt")} />;
  const email = interactive => <div className="email-appearance__preview-area"><div className="email-appearance__email">
    <strong className="email-appearance__hotel">{hotel.name}</strong>
    {header(interactive)}
    <div className="email-appearance__sample"><h2>{tr("sampleTitle")}</h2><p>{tr("sampleBody")}</p><span>{hotel.name}</span></div>
  </div></div>;

  return <section className="email-appearance" aria-labelledby="email-appearance-title">
    <Box direction="row" align="center" justify="between" gap="small" wrap>
      <Heading level={3} margin="none" id="email-appearance-title">{tr("title")}</Heading>
      <Button type="button" icon={<View size="small" aria-hidden="true" />} label={tr("preview")} onClick={() => setPreview(true)} />
    </Box>
    <Text size="small" color="text-weak">{tr("description")}</Text>
    {enabled ? <>
      <Select aria-label={tr("photoMode")} value={design.mode} valueKey={{ key: "value", reduce: true }} labelKey="label" disabled={busy}
        options={[{ value: "default", label: tr("defaultPhoto") }, { value: "custom", label: tr("customPhoto") }, { value: "none", label: tr("noPhoto") }]}
        onChange={({ value }) => { setError(""); setDesign(previous => ({ ...previous, mode: value })); }} />
      {mode === "none" && <Text size="small">{tr("noPhotoHelp")}</Text>}
      {mode === "custom" && <>
        <Box direction="row" align="center" gap="small" wrap>
          <Button type="button" icon={<ImageIcon size="small" aria-hidden="true" />} label={tr(source || saved.header ? "changeImage" : "uploadImage")}
            disabled={busy} onClick={requestUpload} />
          {loading && <Spinner aria-label={tr("preparing")} />}
        </Box>
        <Text size="small" color="text-weak">{tr("fileHelp")}</Text>
        {!saved.source && saved.header && !sourceFile && <Text size="small">{tr("legacyHelp")}</Text>}
        {source && <>
          <Box direction="row" gap="medium" align="center" wrap>
            <CheckBox label={tr("showLogo")} checked={design.showLogo} disabled={!logoAvailable || busy}
              onChange={event => { setError(""); setDesign(previous => ({ ...previous, showLogo: event.target.checked })); }} />
            {design.showLogo && logoAvailable && <Select aria-label={tr("cardPosition")} value={design.cardPosition} valueKey={{ key: "value", reduce: true }} labelKey="label"
              options={[{ value: "left", label: tr("left") }, { value: "right", label: tr("right") }]} disabled={busy}
              onChange={({ value }) => setDesign(previous => ({ ...previous, cardPosition: value }))} />}
            {logoLoading && <Spinner aria-label={tr("preparing")} />}
          </Box>
          {!logoAvailable && <Text size="small">{tr("missingLogo")}</Text>}
          <label className="email-appearance__range"><span>{tr("zoom")}</span>
            <input type="range" min={1} max={3} step={0.01} value={design.zoom} disabled={busy}
              onChange={event => setDesign(previous => ({ ...previous, zoom: Number(event.target.value) }))} />
          </label>
          <Text size="small" id="email-header-drag-help">{tr("cropHelp")}</Text>
        </>}
      </>}
    </> : <>
      <Button type="button" icon={<ImageIcon size="small" aria-hidden="true" />} label={tr("uploadImage")} onClick={requestUpload} />
      <Text size="small" color="text-weak">{tr("upgradeHelp")}</Text>
    </>}
    <input ref={input} type="file" disabled={!enabled || busy} accept={HEADER_TYPES.join(",")} onChange={selectImage} hidden aria-label={tr("uploadImage")} />
    {mode === "custom" && !source && !saved.header ? <Box pad="large" background="light-2" align="center"><Text>{tr("emptyPhoto")}</Text></Box> : email(true)}
    <Text size="small" color="text-weak">{tr("previewHelp")}</Text>
    {error && <Box gap="small"><Text role="alert" color="status-critical">{error}</Text>
      {mode === "custom" && !source && (saved.source || saved.header) && <Button type="button" label={tr("retry")} disabled={busy} onClick={() => setReload(value => value + 1)} />}
    </Box>}
    {enabled && <Box className="email-appearance__actions" direction="row" justify="end" gap="small">
      <Button type="button" label={tr("cancel")} onClick={cancel} disabled={busy || !dirty} />
      <Button type="button" primary label={tr(saving ? "saving" : "save")} onClick={save}
        disabled={busy || !dirty || (mode === "custom" && (!source || logoLoading || (design.showLogo && logoAvailable && !logo)))} />
    </Box>}
    {preview && <Layer onEsc={() => setPreview(false)} onClickOutside={() => setPreview(false)} responsive>
      <Box className="email-appearance__dialog" width="large" overflow="auto" pad="medium" gap="medium">
        <Box direction="row" align="center" justify="between" flex={false}><Heading level={3} margin="none">{tr("preview")}</Heading>
          <Button type="button" icon={<FormClose />} aria-label={tr("close")} onClick={() => setPreview(false)} /></Box>
        {email(false)}<Text size="small">{tr("previewHelp")}</Text>
      </Box>
    </Layer>}
  </section>;
}
